import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { PrizeRevealWindow } from "./prize-reveal-window";
import { PrizeTallyWindow } from "./prize-tally-window";
import { REVEAL_ALL_THRESHOLD, RevealActions } from "./reveal-actions";
import { RevealClaiming, RevealFailed } from "./prize-reveal-pending";
import type { CampaignSchema } from "@/types";
import { cn } from "@/lib/utils";
import { formatPrizeTitle } from "@/utils/helpers";
import { usePendingWinCount } from "@/hooks/use-pending-win-count";
import { usePortalContainer } from "@/contexts/portal-container";
import { useRevealFlow } from "@/hooks/use-reveal-flow";

export interface PrizeRevealCardProps {
  campaign: CampaignSchema;
  /** Reel content — the campaign's droppable prize labels, built by the campaign type's module.
   *  Not the won prize: that is unknown until the claim succeeds (#3922). */
  reveal: { prizes: string[] };
  /** Whether THIS mount owns the portalled reveal window. `detailView` mounts this card twice
   *  (mobile + desktop) and the open state now lives in the store, so both would render the
   *  window — and `hidden lg:flex` does not help, because a portalled child escapes the hidden
   *  wrapper. Exactly one mount per surface passes true. */
  ownsWindow?: boolean;
  className?: string;
}

/**
 * The reveal ceremony's window for `campaign` — the reel for one prize, the
 * tally for a batch — portalled out of the widget's animated wrappers, for
 * as long as its reveal batch has disclosed rows. Rendered by the card's
 * owning mount (`ownsWindow`) and, with the panel closed, by the widget's
 * own root (`ClosedPanelReveal`: a tap on the in-game button's Reveal Prize
 * plays the reveal alone, no dashboard).
 */
export const PrizeRevealWindowFor = ({
  campaign,
  reveal,
}: Pick<PrizeRevealCardProps, "campaign" | "reveal">) => {
  const { t } = useTranslation();
  const pendingCount = usePendingWinCount(campaign);
  const flow = useRevealFlow(campaign);
  // The reveal window is `position: fixed`, but the widget container animates its wrappers with
  // motion (transform => containing block), which would shrink "full screen" to the widget
  // panel. Portal into the shadow tree's theme anchor, which sits outside those wrappers (same
  // seam Radix dialogs use), so the overlay covers the viewport as designed.
  const portalContainer = usePortalContainer();

  // One prize keeps the reel; a bulk reveal gets the tally. The count is the batch's own
  // snapshot, so a win landing mid-reveal can't switch the window out from under the player.
  const window_ =
    flow.rows.length === 0 ? null : flow.count > 1 ? (
      <PrizeTallyWindow
        rows={flow.rows}
        count={flow.count}
        // `confirm`, not `dismiss`, even though a tally normally claimed everything: a win that
        // landed mid-batch is still queued, and it should be offered rather than stranded.
        onConfirm={flow.confirm}
      />
    ) : (
      <PrizeRevealWindow
        // Both lists must arrive in the SAME form. The window matches `wonPrize` against `prizes`
        // by string equality — to pick the reel's landing cell, and to detect a single-tier
        // campaign — so formatting one side only makes the match always miss: the reel would land
        // on a synthesized cell instead of the real tile it represents, and a one-prize campaign
        // would fake a spin. `revealView` formats its reel entries for the in-game surface for
        // exactly this reason. Format only AFTER any raw-label matching is done, never before:
        // `stripFreespinGameName` collapses distinct free-spins prizes to one string.
        prizes={reveal.prizes.map((p) => formatPrizeTitle(p, t))}
        wonPrize={formatPrizeTitle(flow.rows[0].title, t)}
        games={flow.rows[0].games}
        pendingCount={pendingCount}
        // Must be `confirm`, not `dismiss`: this window labels the button "Next Prize" whenever
        // more wins are queued, and `confirm` is what actually claims the next one.
        onConfirm={flow.confirm}
        // Gated on the same threshold as the card's own action pair, so the option does not
        // appear and disappear as the player works down the queue.
        onRevealAll={
          pendingCount >= REVEAL_ALL_THRESHOLD ? flow.confirmAll : undefined
        }
      />
    );

  if (!window_) return null;
  return portalContainer ? createPortal(window_, portalContainer) : window_;
};

/**
 * In-widget entry point to the prize reveal — the "You have won!" card shown in a campaign's
 * detail view. Type-agnostic: any campaign type's `detailView` can render it by supplying the
 * reel content (see the prizedrop module).
 *
 * This is the ONLY way to reveal a win for casinos without a game-provider integration, where
 * the in-game launcher button never mounts. It renders for every target though — confirming here
 * and confirming on the in-game button drive the same store batch, so the two surfaces stay in
 * sync by construction.
 *
 * Since #3922 a reveal is claim-then-open: tapping commits the claim, and the ceremony opens only
 * once the server discloses what was won. The reel cannot spin toward an unknown prize — it
 * freezes its landing plan on mount — so the wait is explicit rather than hidden.
 */
export const PrizeRevealCard = ({
  campaign,
  reveal,
  ownsWindow = false,
  className,
}: PrizeRevealCardProps) => {
  const { t } = useTranslation();
  const pendingCount = usePendingWinCount(campaign);
  const flow = useRevealFlow(campaign);
  return (
    <div
      className={cn(
        "flex w-full flex-col items-start gap-4 overflow-hidden rounded-(--border-radius) border-2 border-primary bg-background-card p-5",
        className
      )}
    >
      <p className="font-heading text-[22px] leading-none font-extrabold text-prizes">
        {t("igwButton.youHaveWon", "You have won!")}
      </p>

      {flow.status === "failed" ? (
        <RevealFailed
          count={flow.count}
          onRetry={flow.retry}
          onDismiss={flow.dismiss}
        />
      ) : flow.status === "claiming" ? (
        <RevealClaiming />
      ) : (
        <>
          <p className="font-body text-[13px] text-foreground-secondary">
            {t("prizeReveal.youHavePrizes", "You have {{count}} Prizes", {
              count: pendingCount,
            })}
          </p>
          <RevealActions
            pendingCount={pendingCount}
            onRevealOne={flow.claimHead}
            onRevealAll={flow.claimAll}
          />
        </>
      )}

      {ownsWindow && (
        <PrizeRevealWindowFor campaign={campaign} reveal={reveal} />
      )}
    </div>
  );
};
